import { Button, cn } from '@newfront-insurance/core-ui/v2';
import { File, X } from 'lucide-react';
import { useRef, type Dispatch, type SetStateAction } from 'react';

import { AttachmentsProvider } from './attachments-provider';
import { ChatAttachmentActions } from './chat-attachment-actions';
import { ChatAttachmentsPreview } from './chat-attachments-preview';
import { ChatInputFrame } from './chat-input';
import { ChatInputProvider } from './chat-input-context';
import { ChatInputTextarea } from './chat-input-textarea';
import { ChatInputToolbar } from './chat-input-toolbar';
import { DragOverlayView } from './drag-overlay';
import type { PromptEditorHandle } from './prompt-editor';
import { useFileSelection, type FileAttachment } from '../../../../hooks/use-file-selection';
import { useChatOverlayContext } from '../../contexts/chat-overlay-context';

export interface NovaPromptFile {
  uuid: string;
  filename: string;
}

interface NovaPromptEditorProps {
  value: string;
  attachments: FileAttachment[];
  existingFiles: NovaPromptFile[];
  accountUuid?: string;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
  onChange: (value: string) => void;
  setAttachments: Dispatch<SetStateAction<FileAttachment[]>>;
  onExistingFilesChange: (files: NovaPromptFile[]) => void;
}

export function NovaPromptEditor({
  value,
  attachments,
  existingFiles,
  accountUuid,
  disabled = false,
  placeholder = 'Describe what Nova should do...',
  className,
  onChange,
  setAttachments,
  onExistingFilesChange,
}: NovaPromptEditorProps) {
  return (
    <AttachmentsProvider attachments={attachments} setAttachments={setAttachments}>
      <NovaPromptEditorContent
        value={value}
        existingFiles={existingFiles}
        accountUuid={accountUuid}
        disabled={disabled}
        placeholder={placeholder}
        className={className}
        onChange={onChange}
        setAttachments={setAttachments}
        onExistingFilesChange={onExistingFilesChange}
      />
    </AttachmentsProvider>
  );
}

function NovaPromptEditorContent({
  value,
  existingFiles,
  accountUuid,
  disabled = false,
  placeholder,
  className,
  onChange,
  setAttachments,
  onExistingFilesChange,
}: Omit<NovaPromptEditorProps, 'attachments'>) {
  const { chatUuid } = useChatOverlayContext();
  const editorRef = useRef<HTMLTextAreaElement | HTMLDivElement>(null);
  const promptEditorRef = useRef<PromptEditorHandle>(null);
  const {
    fileInputRef,
    isDragging,
    handleFileUpload,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    handlePaste,
    handleAttachmentClick,
    handleFolderAttachmentClick,
    supportedFileTypes,
  } = useFileSelection({ setAttachments, chatUuid });
  return (
    <ChatInputProvider
      value={{
        input: value,
        setInput: onChange,
        editorRef,
        promptEditorRef,
        handleSubmit: () => undefined,
        status: 'ready',
        isAtBottom: true,
        scrollToBottom: () => undefined,
      }}
    >
      <ChatInputFrame
        className={className}
        isDragging={isDragging}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <DragOverlayView isDragging={isDragging} />
        <ChatInputTextarea
          placeholder={placeholder}
          ariaLabel="Schedule prompt"
          disabled={disabled}
          skillsAccountUuid={accountUuid}
          preferSkillsContext={false}
          submitOnEnter={false}
          onPasteFiles={handlePaste}
          className="min-h-[100px]"
        />
        <ChatInputToolbar className="border-steel-75 gap-1 border-t">
          <ChatAttachmentActions
            className="shrink-0"
            disabled={disabled}
            fileInputRef={fileInputRef}
            supportedFileTypes={supportedFileTypes}
            onAttachmentClick={handleAttachmentClick}
            onFileUpload={handleFileUpload}
            onFolderAttachmentClick={() => void handleFolderAttachmentClick()}
          />
          <ExistingPromptFiles files={existingFiles} disabled={disabled} onChange={onExistingFilesChange} />
          <ChatAttachmentsPreview />
        </ChatInputToolbar>
      </ChatInputFrame>
    </ChatInputProvider>
  );
}

function ExistingPromptFiles({
  files,
  disabled,
  onChange,
}: {
  files: NovaPromptFile[];
  disabled: boolean;
  onChange: (files: NovaPromptFile[]) => void;
}) {
  if (files.length === 0) return null;

  return (
    <div className="flex min-w-0 items-center gap-1 overflow-x-auto overflow-y-hidden">
      {files.map((file) => (
        <div
          key={file.uuid}
          className="border-steel-200 text-steel-500 inline-flex h-8 max-w-[180px] shrink-0 items-center gap-1.5 rounded-[30px] border bg-white px-2.5 py-1.5"
        >
          <File aria-hidden="true" className="text-steel-400 size-4 shrink-0" />
          <span className="min-w-0 flex-1 truncate text-[12px] leading-4">{file.filename}</span>
          <Button
            type="button"
            variant="transparent"
            size="small"
            aria-label={`Remove ${file.filename}`}
            disabled={disabled}
            onClick={() => onChange(files.filter((candidate) => candidate.uuid !== file.uuid))}
            className={cn(
              'text-steel-400 hover:text-steel-500 h-fit w-fit shrink-0 rounded-full border-none bg-transparent p-0.5',
              'hover:bg-steel-100',
            )}
          >
            <X aria-hidden="true" className="size-3" />
          </Button>
        </div>
      ))}
    </div>
  );
}

export function mergeNovaPromptFiles(existingFiles: NovaPromptFile[], attachments: FileAttachment[]): NovaPromptFile[] {
  const filesByUuid = new Map(existingFiles.map((file) => [file.uuid, file]));
  attachments.forEach((attachment) => {
    if (attachment.fileUuid && !attachment.isUploading && !attachment.uploadError) {
      filesByUuid.set(attachment.fileUuid, {
        uuid: attachment.fileUuid,
        filename: attachment.file.name,
      });
    }
  });
  return [...filesByUuid.values()];
}

export function buildNovaPromptFiles(
  fileUuids: string[] | null | undefined,
  files: NovaPromptFile[],
): NovaPromptFile[] {
  const filesByUuid = new Map(files.map((file) => [file.uuid, file]));
  return (fileUuids ?? []).map(
    (uuid, index) => filesByUuid.get(uuid) ?? { uuid, filename: `Attached file ${index + 1}` },
  );
}

export function hasUnsettledNovaPromptAttachments(attachments: FileAttachment[]): boolean {
  return attachments.some((attachment) => attachment.isUploading || Boolean(attachment.uploadError));
}
